'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProductSectionProps } from '../types';
import { GridIcon, ListIcon, SparkleIcon, FilterIcon } from '../icons';
import ProductCard from './ProductCard';
import ProductCardSkeleton from './ProductCardSkeleton';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { resolveCustomerPrice } from '@/lib/pricing/useExternalPricing';

// Progressive in-memory rendering: API can return up to 1000 hits, but we
// only render the first N cards initially and reveal more in batches.
// Loading triggers ~1.5 viewports before the bottom (large rootMargin), so
// the next batch is already in the DOM before the user reaches the previous
// batch's end — no scroll pause, no flash.
const INITIAL_VISIBLE = 100;
const VISIBLE_BATCH = 100;
const PRELOAD_AHEAD_PX = '1500px';

export default function ProductSection({
  title,
  subtitle,
  products,
  viewMode,
  onViewModeChange,
  isLoading,
  showAiScrollTop,
  onAiScrollTop,
  highlightQuery,
  externalPrices,
  externalPricesLoading = false,
  onOpenFilters,
  filtersOpen = false,
  activeFilterCount = 0,
  fluidGrid = false,
  searchId = null,
  source = 'search',
  onVisibleCountChange,
}: ProductSectionProps) {
  const clientConfig = useClientConfig();
  // Grid columns. Default (search results) keeps the original viewport grid.
  // `fluidGrid` (home/entry screen) drops the `sm:grid-cols-3` step so the
  // popular-products grid shows 2 columns below `lg` — avoiding the cramped
  // 3-up next to the 330px home sidebar on tablet — while desktop (`lg+`)
  // stays at 4 columns exactly as before.
  // The grid is `w-full` (2026-10-01): its parent is `flex flex-col items-start`,
  // which made the grid only as wide as its content. On the entry screen (few
  // short product names) the 4 columns then filled ~2/3 of the area, and card
  // width depended on the font's glyph widths. Results grids were already full
  // width (long names), so they are unchanged.
  const gridColsClass = fluidGrid
    ? 'grid-cols-2 lg:grid-cols-4'
    : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';
  const gridContainerClass = '';
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset window whenever the underlying product list changes (new search,
  // new filter selection, viewMode toggle, etc.)
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [products]);

  // Cap the rendered slice at the available count.
  const visibleProducts = useMemo(
    () => products.slice(0, visibleCount),
    [products, visibleCount],
  );
  const hasMore = visibleCount < products.length;

  // Tell the parent how many products are currently revealed, so it only asks
  // the host for the prices of products someone can actually see (2026-07-28).
  // This grid reveals in batches of 100 on scroll; before this, the parent
  // priced the WHOLE result set up front — up to 1,000 products — even though
  // most shoppers never scroll past the first screen.
  useEffect(() => {
    onVisibleCountChange?.(visibleCount);
  }, [visibleCount, onVisibleCountChange]);

  // Customer prices are fetched by the parent (SearchWidget) for the revealed
  // window and extended as more are revealed. ProductSection just consumes the
  // map. For clients without external pricing, both props stay null/false and
  // `resolveCustomerPrice` returns `undefined`, so cards render RRP unchanged.
  const externalPricingActive = !!(clientConfig.features?.externalPricing && clientConfig.externalPricing);
  const prices = externalPrices ?? null;
  const pricesLoading = externalPricesLoading;

  /**
   * Is THIS product's price still in flight? (2026-07-27, progressive pricing.)
   *
   * Prices now stream in batches, so "the fetch is running" is no longer the
   * same question as "this card has no price yet". Membership in the map is the
   * signal: the endpoint echoes EVERY requested id (unknown ones as
   * `[false,false]`), so an id that is absent while a fetch is still running has
   * simply not been asked for yet.
   *
   * It matters which way this resolves. Absent + still loading must show the
   * SKELETON — showing the catalogue price would flash a number that then
   * changes under the user (founder's call), and `resolveCustomerPrice` would
   * otherwise read the gap as "unavailable" and say «Επικοινωνήστε για τιμή»
   * about a product we simply hadn't reached. Once loading ends, an id still
   * absent means its batch genuinely failed, and the unavailable label is right.
   */
  const isPricePending = (productId: string | number) =>
    externalPricingActive && pricesLoading && !prices?.has(String(productId));

  // IntersectionObserver on the sentinel: when the bottom comes into view,
  // reveal the next batch. Uses a generous rootMargin so cards start
  // appearing before the user actually reaches the end.
  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + VISIBLE_BATCH, products.length));
        }
      },
      { rootMargin: `${PRELOAD_AHEAD_PX} 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, products.length]);
  if (isLoading) {
    return (
      <div className="flex flex-col gap-[24px] items-start w-full">
        {/* Header skeleton */}
        <div className="h-[64px] w-full relative border-b border-[#F8F8F8]">
          <div className="flex items-center gap-[16px] absolute left-[24px] top-[18px] w-[296px]">
            <div className="w-[100px] h-[28px] bg-[#F8F8F8] rounded-[4px] opacity-52" />
            <div className="flex-1 h-[20px] bg-[#F8F8F8] rounded-[4px] opacity-52" />
          </div>
          <div className="flex items-center gap-[16px] absolute right-[24px] top-[20px]">
            <div className="w-[24px] h-[24px] bg-[#F8F8F8] rounded-[4px] opacity-52" />
            <div className="w-[24px] h-[24px] bg-[#F8F8F8] rounded-[4px] opacity-52" />
          </div>
        </div>
        {/* Product grid skeleton */}
        <div className={`flex flex-col items-start px-[24px] w-full ${gridContainerClass}`}>
          <div className={`grid w-full ${gridColsClass} gap-[16px]`}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <ProductCardSkeleton key={i} variant="grid" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col animate-fadeIn w-full">
      {/* Header row — z-20 (above the product-card availability badge's z-10) so
          cards and their absolutely-positioned badges scroll cleanly BEHIND this
          sticky header instead of poking through above it. */}
      <div className="flex items-center justify-between self-stretch pt-6 pb-3 px-6 flex-shrink-0 border-b-[0.5px] border-[#D8D8D8] bg-white sticky top-0 z-20">
        {/* Title + result count stacked (Figma 525:8358) */}
        <div className="flex flex-col items-start">
          <h3 className="text-[16px] font-bold font-gotham text-text-primary leading-[28px]">
            {title}
          </h3>
          {subtitle && (
            <span className="text-[12px] font-normal font-gotham text-[#6A7282] leading-[20px]">
              {subtitle}
            </span>
          )}
        </div>
        {/* View toggle — Figma: 24x24 icons */}
        <div className="flex items-center gap-4">
          {/* Filters trigger — narrow screens only (<lg); on lg+ the persistent
              sidebar is shown instead. Goes brand-coloured while the panel is open
              (Figma 525:7771 default → 525:8310 active). */}
          {onOpenFilters && (
            <button
              onClick={onOpenFilters}
              aria-label={activeFilterCount > 0 ? `Φίλτρα (${activeFilterCount} ενεργά)` : 'Φίλτρα'}
              aria-expanded={filtersOpen}
              className={`relative lg:hidden flex items-center gap-2.5 h-9 px-2.5 cursor-pointer transition-colors ${
                filtersOpen ? 'bg-brand text-brand-text' : 'bg-black text-white hover:bg-black/85'
              }`}
            >
              <FilterIcon className="w-[18px] h-[18px]" color="currentColor" />
              <span className="text-[12px] font-gotham font-medium leading-none">Φίλτρα</span>
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-brand text-brand-text text-[10px] font-gotham font-bold leading-none ring-2 ring-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
          {showAiScrollTop && (
            <button
              onClick={onAiScrollTop}
              className="transition-all duration-300 animate-fadeIn cursor-pointer hover:scale-110"
              aria-label="Show AI suggestions"
            >
              <SparkleIcon className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => onViewModeChange('grid')}
            className={`transition-colors cursor-pointer ${viewMode === 'grid' ? 'text-brand' : ''}`}
            aria-label="Grid view"
          >
            <GridIcon className="w-6 h-6" color="currentColor" />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`transition-colors cursor-pointer ${viewMode === 'list' ? 'text-brand' : ''}`}
            aria-label="List view"
          >
            <ListIcon className="w-6 h-6" color="currentColor" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`flex flex-col items-start gap-4 self-stretch p-6 bg-white ${gridContainerClass}`}>
        {viewMode === 'grid' ? (
          <div className={`grid w-full ${gridColsClass} gap-[16px]`}>
            {visibleProducts.map((product, index) => {
              const customerPrice = resolveCustomerPrice(externalPricingActive, prices, product.id, product.model);
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  variant="grid"
                  index={index}
                  animate={index < INITIAL_VISIBLE}
                  highlightQuery={highlightQuery}
                  customerPrice={customerPrice}
                  loadingCustomerPrice={isPricePending(product.id)}
                  searchId={searchId}
                  source={source}
                />
              );
            })}
          </div>
        ) : (
          <div className="w-full">
            {visibleProducts.map((product, index) => {
              const customerPrice = resolveCustomerPrice(externalPricingActive, prices, product.id, product.model);
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  variant="list"
                  index={index}
                  animate={index < INITIAL_VISIBLE}
                  highlightQuery={highlightQuery}
                  customerPrice={customerPrice}
                  loadingCustomerPrice={isPricePending(product.id)}
                  searchId={searchId}
                  source={source}
                />
              );
            })}
          </div>
        )}
        {/* Sentinel: when this scrolls into view, the next batch reveals. */}
        {hasMore && <div ref={sentinelRef} className="h-4 w-full" aria-hidden />}
      </div>
    </div>
  );
}

